/**
 * 세션 피드백 API 클라이언트
 * @description 세션 피드백 관련 모든 API 호출을 담당합니다.
 */
import instance from "./instance";
import { ENV } from "@/constants/env";

// ✅ PREFIX_PATH 사용 필수 (하드코딩 금지)
const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

export interface SessionFeedbackDto {
  feedback?: string;
}

export interface SessionFeedbackResponse {
  success: boolean;
  message: string;
  feedback?: string;
}

export interface ResponseDto<T> {
  success: boolean;
  message: string;
  data: T;
}

export const sessionFeedbackApi = {
  /**
   * 세션 피드백을 조회합니다.
   * @param sessionId 세션 ID
   * @returns 피드백 내용
   */
  async getFeedback(sessionId: number): Promise<string | null> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}/feedback`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "GET",
      sessionId,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    try {
      const response = await instance.get<SessionFeedbackResponse>(apiUrl);

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.data.success,
        hasFeedback: !!response.data.feedback,
        fullResponse: response.data,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to get feedback");
      }

      return response.data.feedback || null;
    } catch (error: any) {
      console.error("[API_ERROR]", {
        api: "getFeedback",
        sessionId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  /**
   * 세션 피드백을 생성합니다.
   * @param sessionId 세션 ID
   * @param feedback 피드백 내용
   * @returns 생성된 피드백 내용
   */
  async createFeedback(sessionId: number, feedback: string): Promise<string> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}/feedback`;
    const requestData: SessionFeedbackDto = { feedback };

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "POST",
      sessionId,
      feedback: feedback.substring(0, 100) + "...",
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    try {
      const response = await instance.post<SessionFeedbackResponse>(
        apiUrl,
        requestData
      );

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.data.success,
        message: response.data.message,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to create feedback");
      }

      return response.data.feedback || feedback;
    } catch (error: any) {
      console.error("[API_ERROR]", {
        api: "createFeedback",
        sessionId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  /**
   * 세션 피드백을 수정합니다.
   * @param sessionId 세션 ID
   * @param feedback 수정할 피드백 내용
   * @returns 수정된 피드백 내용
   */
  async updateFeedback(sessionId: number, feedback: string): Promise<string> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}/feedback`;
    const requestData: SessionFeedbackDto = { feedback };

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "PUT",
      sessionId,
      feedback: feedback.substring(0, 100) + "...",
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    try {
      const response = await instance.put<SessionFeedbackResponse>(
        apiUrl,
        requestData
      );

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.data.success,
        message: response.data.message,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to update feedback");
      }

      return response.data.feedback || feedback;
    } catch (error: any) {
      console.error("[API_ERROR]", {
        api: "updateFeedback",
        sessionId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },

  /**
   * 세션 피드백을 삭제합니다.
   * @param sessionId 세션 ID
   */
  async deleteFeedback(sessionId: number): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/super-chat-log/sessions/${sessionId}/feedback`;

    console.log("[API_CALL]", {
      url: apiUrl,
      method: "DELETE",
      sessionId,
      PREFIX_PATH,
      baseURL: ENV.API_URL,
    });

    try {
      const response = await instance.delete<SessionFeedbackResponse>(apiUrl);

      console.log("[API_RESPONSE]", {
        url: apiUrl,
        success: response.data.success,
        message: response.data.message,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to delete feedback");
      }
    } catch (error: any) {
      console.error("[API_ERROR]", {
        api: "deleteFeedback",
        sessionId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  },
};
